/**
 * filterPersistence.js
 *
 * Serialises and deserialises the activeFilters object to/from localStorage,
 * keyed by project ID.
 *
 * localStorage key format: "projectFilters:<projectId>"
 *
 * Handles Set ↔ Array conversion since JSON does not support Sets natively.
 * Unknown keys in the stored value are silently ignored so stale saved data
 * (from an older schema) never causes a runtime error.
 *
 * Extending to a new filter type:
 *   1. Add the key to FILTER_KEYS with its type ("set" or "value").
 *   2. Add the key to EMPTY_FILTERS in projectHomeAgGrid.jsx.
 *   Everything else is handled automatically.
 *
 * Schema versioning:
 *   CURRENT_SCHEMA_VERSION is embedded in every saved blob as _schemaVersion.
 *   Increment it when the filter_state shape changes in a breaking way.
 *   loadFilters discards blobs whose version is older than MIN_SUPPORTED_VERSION.
 */

const STORAGE_PREFIX = 'projectFilters:';

/** Keys that are Sets (serialised as arrays). All others are plain JSON values. */
const SET_KEYS = new Set(['entityIds', 'deptIds', 'disabledChangeIds']);

/** Current schema version. Embed in every saved blob. */
const CURRENT_SCHEMA_VERSION = 1;

/**
 * Minimum schema version we can safely load. Blobs with a lower version are
 * discarded so stale shapes from old deployments don't cause silent wrong behaviour.
 * Keep at 1 until a breaking shape change requires bumping it.
 */
const MIN_SUPPORTED_VERSION = 1;

/**
 * Persist activeFilters for a project to localStorage.
 *
 * @param {string|number} projectId
 * @param {object} filters — the activeFilters object
 */
export const saveFilters = (projectId, filters) => {
  if (!projectId) return;
  try {
    const serialised = {};
    Object.entries(filters).forEach(([key, value]) => {
      serialised[key] = SET_KEYS.has(key) ? [...value] : value;
    });
    // Embed metadata for staleness detection (mirrors serialiseFilters in projectHomeAgGrid).
    serialised._projectId     = projectId;
    serialised._schemaVersion = CURRENT_SCHEMA_VERSION;
    localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(serialised));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded, etc.)
    // Fail silently — persistence is a best-effort enhancement.
  }
};

/**
 * Load persisted filters for a project from localStorage.
 * Returns null if nothing is stored, the stored value is malformed, or the
 * schema version is too old to be safely loaded.
 *
 * @param {string|number} projectId
 * @param {object} emptyFilters — the EMPTY_FILTERS constant (used as fallback shape)
 * @returns {object|null} Restored activeFilters object, or null if nothing saved
 */
export const loadFilters = (projectId, emptyFilters) => {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${projectId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    // Discard blobs from schema versions we no longer support.
    // Treat a missing _schemaVersion as CURRENT_SCHEMA_VERSION (first deploy
    // of the schema versioning feature — the blob shape is identical to v1).
    const version = parsed._schemaVersion ?? CURRENT_SCHEMA_VERSION;
    if (version < MIN_SUPPORTED_VERSION) {
      console.warn(`[loadFilters] Discarding stale filter blob for project ${projectId} (schema v${version} < min v${MIN_SUPPORTED_VERSION})`);
      localStorage.removeItem(`${STORAGE_PREFIX}${projectId}`);
      return null;
    }

    // Start from a fresh empty shape (not a reference to the caller's emptyFilters)
    // so we never alias shared mutable Sets.
    const restored = {};
    Object.keys(emptyFilters).forEach(key => {
      restored[key] = SET_KEYS.has(key) ? new Set() : emptyFilters[key];
    });
    Object.entries(parsed).forEach(([key, value]) => {
      if (!(key in emptyFilters)) return; // ignore unknown/metadata keys (e.g. _projectId, _schemaVersion)
      if (SET_KEYS.has(key)) {
        restored[key] = Array.isArray(value) ? new Set(value) : new Set();
      } else {
        restored[key] = value;
      }
    });
    return restored;
  } catch {
    return null;
  }
};

/**
 * Remove persisted filters for a project (e.g. on project delete).
 *
 * @param {string|number} projectId
 */
export const clearSavedFilters = (projectId) => {
  if (!projectId) return;
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${projectId}`);
  } catch {
    // ignore
  }
};

const LAST_ACTIVE_KEY = 'lastActiveProjectId';

/**
 * Record which project the user was most recently working in.
 * Called alongside saveFilters so the "last active" concept stays in sync.
 *
 * @param {string|number} projectId
 */
export const setLastActiveProjectId = (projectId) => {
  if (!projectId) return;
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(projectId));
  } catch {
    // ignore
  }
};

/**
 * Return the project ID the user was most recently working in, or null.
 * Used by applyInitialFilters to decide whether to apply a default saved
 * view (new project) or restore last-used filters (same project as before).
 *
 * @returns {string|null}
 */
export const getLastActiveProjectId = () => {
  try {
    return localStorage.getItem(LAST_ACTIVE_KEY) ?? null;
  } catch {
    return null;
  }
};
